/**
 * TemplateListItem — compact template row for the split-view list.
 *
 * Extracted as-is from BlogTemplatesPage (Phase 1 decomposition); the source
 * had no tag affordance, so nothing was removed.
 */

import React from 'react';
import { Sparkles, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BlogTemplateSummary } from '@/services/blogTemplatesService';

export interface TemplateListItemProps {
  template: BlogTemplateSummary;
  isSelected: boolean;
  onSelect: (template: BlogTemplateSummary) => void;
}

export const TemplateListItem = React.memo<TemplateListItemProps>(({ template, isSelected, onSelect }) => {
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-150 border",
        isSelected
          ? "bg-primary/10 border-primary shadow-sm"
          : "bg-card hover:bg-muted border-transparent hover:border-border"
      )}
      onClick={() => onSelect(template)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{template.name}</span>
          {template.is_popular && (
            <Star className="h-3 w-3 text-amber-500 dark:text-amber-400 flex-shrink-0" />
          )}
          {template.is_new && (
            <Sparkles className="h-3 w-3 text-green-500 dark:text-green-400 flex-shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
          {template.description || 'No description'}
        </p>
      </div>
      {isSelected && (
        <div className="w-1 h-8 bg-primary rounded-full flex-shrink-0" />
      )}
    </div>
  );
});

TemplateListItem.displayName = 'TemplateListItem';
